import { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight, Clock, Car, Phone, CalendarDays, Truck } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_DOT = {
  pending: "bg-yellow-400",
  confirmed: "bg-green-400",
  completed: "bg-zinc-400",
  cancelled: "bg-red-400",
  reschedule: "bg-amber-400",
  gcal: "bg-[#3b82f6]", // Google Calendar events
  dropoff: "bg-[#f59e0b]", // Car drop-off dates
};

const STATUS_COLORS = {
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
  completed: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
  reschedule: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  gcal: "bg-[#3b82f6]/20 text-[#3b82f6] border-[#3b82f6]/30",
  dropoff: "bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30",
};

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function parseApptDate(dateStr) {
  // Parse "Monday 3 March 2026" or "Monday, 3 March 2026" style dates
  try {
    const cleaned = dateStr.replace(/,/g, '').trim();
    const parts = cleaned.split(/\s+/);
    // Find day number, month name, year
    let day, month, year;
    for (const p of parts) {
      const num = parseInt(p);
      if (!isNaN(num) && num > 0 && num <= 31 && !day) day = num;
      else if (!isNaN(num) && num > 1900) year = num;
      else {
        const mIdx = MONTHS.findIndex(m => m.toLowerCase().startsWith(p.toLowerCase()));
        if (mIdx !== -1) month = mIdx;
      }
    }
    if (day !== undefined && month !== undefined && year !== undefined) {
      return new Date(year, month, day);
    }
  } catch (e) {
    // Date parsing failed, fall through to native parse
  }
  // Fallback: try native parse
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

export function DashboardCalendar({ appointments, gcalConnected = false, dropoffs = [] }) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { month: now.getMonth(), year: now.getFullYear() };
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [gcalEvents, setGcalEvents] = useState([]);

  // Fetch Google Calendar events when connected
  useEffect(() => {
    if (gcalConnected) {
      axios.get(`${API}/calendar/events?days=60`)
        .then(res => {
          const events = res.data.events || [];
          // Transform Google Calendar events to match appointment format
          const transformed = events.map(e => ({
            id: e.id,
            name: e.summary || "Google Calendar Event",
            preferred_date: e.start?.dateTime || e.start?.date,
            preferred_time: e.start?.dateTime 
              ? new Date(e.start.dateTime).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
              : "All day",
            status: "gcal",
            service_type: e.description || "External Event",
            isGcal: true,
            location: e.location || "",
          }));
          setGcalEvents(transformed);
        })
        .catch(err => {
          // Silently fail - Google Calendar events won't show but website appointments will
          console.log("Google Calendar sync not available - website appointments will still show");
          setGcalEvents([]);
        });
    } else {
      setGcalEvents([]);
    }
  }, [gcalConnected]);

  // Map appointments to dates (including Google Calendar events and drop-offs)
  const apptsByDate = useMemo(() => {
    const map = {};
    
    // Add website appointments
    appointments.forEach((a) => {
      const d = parseApptDate(a.preferred_date);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    
    // Add car drop-offs (quotes with expected_delivery_date)
    dropoffs.forEach((q) => {
      if (!q.expected_delivery_date) return;
      const d = new Date(q.expected_delivery_date);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push({
        ...q,
        status: 'dropoff',
        preferred_date: q.expected_delivery_date,
        preferred_time: '09:00 AM',
        vehicle_model: q.vehicle_model,
        isDropoff: true
      });
    });
    
    // Add Google Calendar events
    gcalEvents.forEach((e) => {
      const d = new Date(e.preferred_date);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      // Don't add duplicates (events that were synced from website)
      const isDuplicate = map[key].some(a => 
        a.name === e.name || (a.gcal_event_id && a.gcal_event_id === e.id)
      );
      if (!isDuplicate) {
        map[key].push(e);
      }
    });
    
    return map;
  }, [appointments, gcalEvents, dropoffs]);

  const { month, year } = currentMonth;
  const firstDay = new Date(year, month, 1);
  // Monday = 0, Sunday = 6
  let startDay = firstDay.getDay() - 1;
  if (startDay < 0) startDay = 6;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const prevMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 0) return { month: 11, year: prev.year - 1 };
      return { ...prev, month: prev.month - 1 };
    });
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 11) return { month: 0, year: prev.year + 1 };
      return { ...prev, month: prev.month + 1 };
    });
    setSelectedDate(null);
  };

  const goToday = () => {
    setCurrentMonth({ month: today.getMonth(), year: today.getFullYear() });
    setSelectedDate(`${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`);
  };

  // Build calendar grid
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedAppts = selectedDate ? (apptsByDate[selectedDate] || []) : [];

  return (
    <div data-testid="dashboard-calendar" className="grid grid-cols-1 lg:grid-cols-3 gap-[1px] bg-white/5">
      {/* Calendar Grid */}
      <div className="lg:col-span-2 bg-[#09090b] p-6 md:p-8">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={prevMonth} data-testid="cal-prev"
            className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white transition-colors bg-transparent border border-white/10 hover:border-white/20 cursor-pointer">
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center gap-4">
            <h3 className="font-heading text-xl uppercase tracking-wide text-white">
              {MONTHS[month]} {year}
            </h3>
            <button onClick={goToday} data-testid="cal-today"
              className="px-3 py-1 text-xs tracking-[0.1em] uppercase font-body border border-white/10 text-zinc-500 hover:text-white hover:border-white/20 transition-colors bg-transparent cursor-pointer">
              Today
            </button>
          </div>
          <button onClick={nextMonth} data-testid="cal-next"
            className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white transition-colors bg-transparent border border-white/10 hover:border-white/20 cursor-pointer">
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-[1px] mb-[1px]">
          {DAYS.map((d) => (
            <div key={d} className="text-center text-xs tracking-[0.15em] uppercase text-zinc-600 font-body py-2">
              {d}
            </div>
          ))}
        </div>

        {/* Date Cells */}
        <div className="grid grid-cols-7 gap-[1px]">
          {cells.map((day, i) => {
            if (day === null) {
              return <div key={`empty-${i}`} className="aspect-square bg-[#050505]" />;
            }
            const dateKey = `${year}-${month}-${day}`;
            const dayAppts = apptsByDate[dateKey] || [];
            const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
            const isSelected = selectedDate === dateKey;

            return (
              <button
                key={dateKey}
                onClick={() => setSelectedDate(isSelected ? null : dateKey)}
                data-testid={`cal-day-${day}`}
                className={`aspect-square bg-[#0a0a0a] p-1.5 flex flex-col items-start cursor-pointer border transition-colors ${
                  isSelected
                    ? "border-[#3b82f6]/50 bg-[#3b82f6]/5"
                    : isToday
                    ? "border-white/20"
                    : "border-transparent hover:border-white/10"
                }`}
              >
                <span className={`text-xs font-mono ${
                  isToday ? "text-[#3b82f6] font-bold" : "text-zinc-500"
                }`}>
                  {day}
                </span>
                {dayAppts.length > 0 && (
                  <div className="flex flex-wrap gap-0.5 mt-auto w-full">
                    {dayAppts.slice(0, 4).map((a, ai) => (
                      <div
                        key={ai}
                        className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[a.status] || STATUS_DOT.pending}`}
                        title={`${a.name} - ${a.service_type} (${a.status})`}
                      />
                    ))}
                    {dayAppts.length > 4 && (
                      <span className="text-[8px] text-zinc-600 font-mono">+{dayAppts.length - 4}</span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-white/5">
          {Object.entries(STATUS_DOT).map(([status, color]) => (
            <div key={status} className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${color}`} />
              <span className="text-xs text-zinc-600 font-body capitalize">
                {status === 'gcal' ? 'Google Cal' : status === 'dropoff' ? 'Drop-off' : status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Day Detail Panel */}
      <div className="bg-[#09090b] p-6 md:p-8">
        <h4 className="font-heading text-sm tracking-[0.2em] uppercase text-zinc-400 mb-4">
          {selectedDate
            ? `${new Date(year, month, parseInt(selectedDate.split('-')[2])).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}`
            : "Select a date"
          }
        </h4>

        {!selectedDate ? (
          <p className="text-zinc-700 font-body text-sm">Click a day on the calendar to view appointments.</p>
        ) : selectedAppts.length === 0 ? (
          <p className="text-zinc-700 font-body text-sm">No appointments on this day.</p>
        ) : (
          <div className="space-y-3 overflow-y-auto max-h-[500px]">
            {selectedAppts.map((a) => (
              <div key={a.id} data-testid={`cal-appt-${a.id}`}
                className={`border p-4 transition-colors ${
                  a.isGcal ? 'border-[#3b82f6]/20 hover:border-[#3b82f6]/40' : 
                  a.isDropoff ? 'border-[#f59e0b]/20 hover:border-[#f59e0b]/40 bg-[#f59e0b]/5' : 
                  'border-white/5 hover:border-white/10'
                }`}>
                <div className="flex items-center gap-2 mb-2">
                  {a.isGcal && <CalendarDays size={12} className="text-[#3b82f6]" />}
                  {a.isDropoff && <Truck size={12} className="text-[#f59e0b]" />}
                  <h5 className="font-heading text-sm uppercase tracking-wide text-white">{a.name}</h5>
                  <span className={`px-1.5 py-0.5 text-[10px] uppercase font-mono border ${STATUS_COLORS[a.status] || STATUS_COLORS.pending}`}>
                    {a.isGcal ? 'GCAL' : a.isDropoff ? 'DROP-OFF' : a.status}
                  </span>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center gap-2 text-zinc-500 font-mono">
                    <Clock size={10} />
                    <span>{a.preferred_time}</span>
                  </div>
                  {a.vehicle_model && (
                    <div className="flex items-center gap-2 text-zinc-500 font-mono">
                      <Car size={10} />
                      <span>{a.vehicle_model}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-zinc-400 font-body">
                    <span>{a.service_type}</span>
                  </div>
                  {a.location && (
                    <div className="flex items-center gap-2 text-zinc-600 font-body">
                      <span>📍 {a.location}</span>
                    </div>
                  )}
                  {a.phone && (
                    <div className="flex items-center gap-2 text-zinc-600 font-mono">
                      <Phone size={10} />
                      <span>{a.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
